import type { ComponentType } from 'react'
import { registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { InlineEditor } from '../InlineEditor'
import { formatDate } from '../../locale'

const pad = (n: number) => String(n).padStart(2, '0')

function parse(value: unknown): Date | null {
  if (value == null || value === '') return null
  const d = new Date(value as string | number)
  return Number.isNaN(d.getTime()) ? null : d
}

/** E6 · date content — locale date, no time. */
function DateContent({ value }: CellContentProps) {
  const d = parse(value)
  return d ? (
    <span className="tabular-nums">{formatDate(d, 'date')}</span>
  ) : (
    <span className="text-muted-foreground">—</span>
  )
}

/** E6 · timestamp content — locale date + time. */
function DateTimeContent({ value }: CellContentProps) {
  const d = parse(value)
  return d ? (
    <span className="tabular-nums">{formatDate(d, 'dateTime')}</span>
  ) : (
    <span className="text-muted-foreground">—</span>
  )
}

/** Local-midnight epoch ms for a Date — strips time-of-day so a difference
 *  measures whole CALENDAR days, not raw elapsed milliseconds. */
function localMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** SEN-1766 — whole weeks between `d` and `now`, signed (past positive). Pure
 *  and injectable so the test does not depend on the wall clock.
 *
 * R4 minor #2 (fix round 1): computed on CALENDAR days, not raw elapsed ms.
 * `Math.trunc(Δms / 6.048e8)` measured absolute elapsed time, so 14 calendar
 * days spanning a DST transition was 13d23h → truncated to 1 week instead of
 * 2 (the suite runs under TZ=UTC — pinned by `test.env` in
 * poiesis/vitest.config.ts — which has no DST, so this never showed there).
 * Normalizing both instants to LOCAL midnight first, then rounding the day
 * difference, absorbs a DST transition's one-off short/long day: the total
 * elapsed ms between two local-midnight instants already reflects every
 * daylight-saving shift crossed, and rounding (not truncating) recovers the
 * exact whole-day count from that slightly-off-24h-per-day total. */
export function weeksSince(d: Date, now: Date): number {
  const days = Math.round((localMidnight(now) - localMidnight(d)) / 86_400_000)
  return Math.trunc(days / 7)
}

const WEEKS_FMT = new Intl.NumberFormat('en-GB')

/** SEN-1766 · date content, `weeks_since` variant — the same cell read as an
 *  AGE. One codec, one attribute, two column entries. */
function WeeksSinceContent({ value }: CellContentProps) {
  const d = parse(value)
  if (!d) return <span className="text-muted-foreground">—</span>
  const w = weeksSince(d, new Date())
  const text = w === 0 ? 'this week' : w > 0 ? `${WEEKS_FMT.format(w)}w ago` : `in ${WEEKS_FMT.format(-w)}w`
  return <span data-testid="cell-format-weeks_since" className="tabular-nums">{text}</span>
}

/** The kind's format dispatcher: the entry's declared variant picks the
 *  content renderer from THIS kind's own table. Unknown variant → the default
 *  (annotate-not-block: a cell never goes blank because a format drifted). */
const DATE_VARIANTS: Record<string, ComponentType<CellContentProps>> = {
  weeks_since: WeeksSinceContent,
}

function DateCell(props: CellContentProps) {
  const Variant = props.format ? DATE_VARIANTS[props.format] : undefined
  return Variant ? <Variant {...props} /> : <DateContent {...props} />
}

/**
 * date editor — native date picker through the ONE InlineEditor seam. The
 * commit contract (cell: Enter/blur; filter: per-change — SEN-704) lives in
 * InlineEditor ONCE; date differs only by toDraft/parse/inputProps. (The old
 * "never mid-change" comment applied to CELL context, where commit closes the
 * editor — that semantic is preserved because live commit is filter-only.)
 */
function DateEditor(props: CellEditorProps) {
  return (
    <InlineEditor
      {...props}
      toDraft={(v) => {
        const d = parse(v)
        return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : ''
      }}
      parse={(s) => s || null}
      inputProps={{ type: 'date' }}
    />
  )
}

/** timestamp editor — same seam; drafts datetime-local, commits ISO (UTC). */
function DateTimeEditor(props: CellEditorProps) {
  return (
    <InlineEditor
      {...props}
      toDraft={(v) => {
        const d = parse(v)
        return d
          ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
          : ''
      }}
      parse={(s) => (s ? new Date(s).toISOString() : null)}
      inputProps={{ type: 'datetime-local' }}
    />
  )
}

registerWidget('date', {
  read: DateCell,
  write: DateEditor,
  defaultWidth: 150,
  formats: [
    { value: 'date', label: 'Date' },
    { value: 'weeks_since', label: 'Weeks since' },
  ],
})
registerWidget('timestamp', { read: DateTimeContent, write: DateTimeEditor, defaultWidth: 150 })
